import {
  DateFilterValue,
  FilterDefinition,
  FilterState,
  FilterStateApi,
  FiltersValues,
  FilterValues
} from '../../../../../types'
import { DateRangeSelection, getRelativeRange } from '@contrast-security-inc/design-system-inputs'

// date filter structure
// { startDate: new Date(), endDate: new Date(), type: "LAST_7_DAYS"}

// URL write
const updateUrl = (
  filters: Record<string, FilterValues>,
  filterName: string,
  updatedUrlSearchParams: URLSearchParams
) => {
  const dateFilter = filters[filterName] as DateFilterValue

  updatedUrlSearchParams.append(`${filterName}-type`, encodeURIComponent(dateFilter.type))

  if (dateFilter.type === 'RELATIVE_TO_NOW') {
    updatedUrlSearchParams.append(`${filterName}-unit`, encodeURIComponent(dateFilter.unit))
    updatedUrlSearchParams.append(
      `${filterName}-count`,
      encodeURIComponent(dateFilter.count.toString())
    )
  } else {
    // we don't need to store start/end date for relative filters because
    // they will be recalculated on page load
    updatedUrlSearchParams.append(
      `${filterName}-startDate`,
      encodeURIComponent(dateFilter.startDate.getTime().toString())
    )
    updatedUrlSearchParams.append(
      `${filterName}-endDate`,
      encodeURIComponent(dateFilter.endDate.getTime().toString())
    )
  }
}

const updateFilter = ({
  filterKey,
  dateRange
}: { dateRange: DateRangeSelection } & Pick<FilterState, 'filterKey'>): FiltersValues => {
  return { [filterKey as string]: dateRange }
}

const getFilterCount = (filters: Record<string, DateRangeSelection>, filterKey: string) => {
  return filters[filterKey].startDate ? 1 : 0
}

// URL read
const readUrl = (
  filterDef: FilterDefinition,
  urlParams: URLSearchParams,
  prevFilters?: FiltersValues
) => {
  const paramFilterKey = `${filterDef.filterKey}-`

  let selectedDate = {} as DateRangeSelection

  Array.from(urlParams.keys()) // grab all url params
    .filter((paramKey: string) => paramKey.startsWith(paramFilterKey)) // only keys for this def
    .reduce<string[]>(
      (acc: string[], it: string) => (acc.indexOf(it) !== -1 ? acc : [...acc, it]),
      []
    ) // unique keys
    .forEach((kvParamKey) => {
      const fieldKey = kvParamKey.substring(paramFilterKey.length)
      const fieldValue = urlParams.get(kvParamKey)

      if (fieldKey === 'startDate' || fieldKey === 'endDate') {
        selectedDate[fieldKey] = new Date(Number(fieldValue))
      } else if (fieldKey === 'count') {
        // @ts-ignore we know the count is a number
        selectedDate.count = Number(fieldValue)
      } else {
        // @ts-ignore assume the remaing properties exist and are strings
        selectedDate[fieldKey] = fieldValue
      }
    })

  // Keep previous values if the relative date range is the same
  // otherwise, recalculate the relative date range
  // This is useful for keeping the URL clean of start/end dates for relative range
  // especially since we want to recalculate the relative range on page load
  if (selectedDate.type === 'RELATIVE_TO_NOW') {
    const prevDate = prevFilters && (prevFilters[filterDef.filterKey] as DateRangeSelection)

    if (
      prevDate?.type === selectedDate.type &&
      prevDate.unit === selectedDate.unit &&
      prevDate.count === selectedDate.count
    ) {
      selectedDate = prevDate
    } else {
      selectedDate = getRelativeRange({
        count: selectedDate.count,
        unit: selectedDate.unit,
        type: selectedDate.type
      })
    }
  }

  return selectedDate.startDate ? selectedDate : (undefined as unknown as DateRangeSelection)
}

export const stateApi: FilterStateApi = {
  // @ts-ignore we know this specific filter type is valid
  getFilterCount,
  // @ts-ignore we know this specific filter type is valid
  updateFilter,
  // @ts-ignore we know this specific filter type is valid
  updateUrl,
  readUrl
}
